<template>
  <v-container fluid class="py-4 px-4">
    <!-- Toolbar -->
    <v-app-bar app :color="isDark ? '#2A2A2A' : 'primary'" :dark="isDark">
      <!-- Back Button -->
      <v-btn icon @click="goToHome" class="back-btn">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <v-toolbar-title>
        <v-btn
  color="white"
  :variant="activeView === 'invitations' ? 'filled' : 'tonal'"
  @click="activeView = 'users'"
>
<v-icon>mdi-account-group</v-icon>
&nbsp; Manage Users
</v-btn>

<v-btn
  color="white"
  :variant="activeView === 'users' ? 'filled' : 'tonal'"
  @click="activeView = 'invitations'"
>
<v-icon>mdi-email-open</v-icon>
&nbsp; Manage Invitations
</v-btn>

      </v-toolbar-title>

      <v-spacer></v-spacer>
<!-- Single Invite Button -->
<v-btn color="white" class="toolbar-btn" @click="openInviteDialog">
  <v-icon>mdi-email-plus</v-icon>
  &nbsp; Invite User
</v-btn>

<!-- Bulk Invite Button -->
<v-btn color="white" class="toolbar-btn" @click="openBulkInviteDialog">
  <v-icon>mdi-account-multiple-plus</v-icon>
  &nbsp; Bulk Invite
</v-btn>
      <!-- Add User Button -->
      <v-btn color="white" class="toolbar-btn" @click="createUser">
        <v-icon>mdi-account-plus</v-icon>
        &nbsp; Add User
      </v-btn>
    </v-app-bar>

    <!-- Loading Spinner -->
    <v-row justify="center" align="center" v-if="loading" class="my-8">
      <v-progress-circular indeterminate color="primary" size="70"></v-progress-circular>
    </v-row>

    <!-- Users Table -->
    <div v-if="activeView === 'users'">
    <v-card flat>
      <v-card-title class="d-flex align-center pe-2">

        <!-- Search Field -->
        <v-text-field
          v-model="search"
          density="compact"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        v-model:search="search"
        :headers="headers"
        :items="users"
        item-value="id"
        class="elevation-2"
        dense
        v-model:items-per-page="itemsPerPage"
        :items-per-page-options="[5, 10, 25, 50, { value: -1, title: 'All' }]"
      >
        <template v-slot:body="{ items }">
          <tr v-for="user in items" :key="user.id" class="clickable-row">
            <td>
              <v-avatar
                  :key="user.id"
                  size="32"
                  class="me-1"
                  :title="user.username"
                >
                  <img :src="getImageUrl(user.image)" alt="User's image" class="circle-image"/>
                </v-avatar>

              {{ user.username }}



            </td>
            <td>{{ user.email }}</td>
            <td>
              <v-chip
                v-for="role in user.roles"
                :key="role"
                class="me-2"
                :color="getRoleColor(role)"
                dark
                small
              >
                <v-icon start>{{ getRoleIcon(role) }}</v-icon>
                {{ getRoleText(role) }}
              </v-chip>
            </td>
            <td >
<!-- Edit Button -->
<v-btn
  class="edit-btn"
  @click.stop="editUser(user)"
  title="Edit User"
  elevation="2"
>
  <v-icon color="cyan" small>mdi-pencil</v-icon>
</v-btn>

<!-- Delete Button -->
<v-btn
  class="delete-btn"
  @click.stop="deleteUser(user.id)"
  title="Delete User"
  elevation="2"
>
  <v-icon color="red-darken-2" small>mdi-close</v-icon>
</v-btn>

            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
<!-- Invitations Table (visible only if user is Admin; you can check user roles) -->
<div v-else-if="activeView === 'invitations'">
<v-card flat class="mt-4">
  <v-card-title><v-icon>mdi-account-clock</v-icon>&nbsp; Pending Invitations</v-card-title>
  <v-data-table
    :headers="inviteHeaders"
    :items="invitations"
    item-value="id"
    class="elevation-2"
    dense
  >
    <template v-slot:body="{ items }">
      <tr v-for="invite in items" :key="invite.id">
        <td>{{ invite.email }}</td>
        <td>              <v-chip
                class="me-2"
                :color="getRoleColor(invite.role)"
                dark
                small
              >
                <v-icon start>{{ getRoleIcon(invite.role) }}</v-icon>
                {{ getRoleText(invite.role) }}
              </v-chip></td>
        <td>
          <span v-if="invite.project" class="text-caption">{{ invite.project.name }}</span>
          <span v-else class="text-grey text-caption">—</span>
        </td>
        <td>  <v-chip
    class="me-2"
    :color="inviteStatus(invite).color"
    dark
    small
  >
    <v-icon start>{{ inviteStatus(invite).icon }}</v-icon>
    {{ inviteStatus(invite).text }}
  </v-chip></td>
        <td>{{ formatDate(invite.expires_at)  }}</td>
        <td>
<!-- Cancel Invite Button -->
<v-btn
  class="cancel-invite-btn"
  v-if="invite.is_active"
  @click="cancelInvite(invite.id)"
  elevation="2"
>
  <v-icon color="red-darken-2" small>mdi-close</v-icon>
</v-btn>
        </td>
      </tr>
    </template>
  </v-data-table>
</v-card>
</div>
    <!-- Dialog for Creating or Editing Users -->
    <v-dialog v-model="showCreateUserDialog" max-width="500px" @keydown.enter="valid && validateAndSaveUser()" @keydown.esc="closeDialog">
      <v-card>
        <v-card-title>{{ isEditing ? 'Edit User' : 'Create User' }}</v-card-title>
        <v-card-text>
          <v-form ref="userForm" v-model="valid">
            <v-text-field
              label="Username"
              v-model="form.username"
              :rules="[v => !!v || 'Username is required']"
              required
            ></v-text-field>
            <v-text-field
              label="Email"
              v-model="form.email"
              :rules="[v => !!v || 'Email is required', v => /.+@.+\..+/.test(v) || 'Invalid email']"
              required
            ></v-text-field>
            <v-select
              label="Role"
              v-model="form.roles"
              :items="roles"
              item-title="title"
              item-value="value"
              multiple
              :menu-props="{ closeOnContentClick: false }"
              required
              :rules="[v => v && v.length > 0 || 'At least one role is required']"
            >
              <template v-slot:selection="{ item }">
                <v-chip
                  :color="getRoleColor(item.value)"
                  small
                  dark
                >
                  <v-icon start>{{ getRoleIcon(item.value) }}</v-icon>
                  {{ item.title }}
                </v-chip>
              </template>
            </v-select>

            <v-text-field
              label="Password"
              v-model="form.password"
              :type="'password'"
              :rules="isEditing ? [] : [v => !!v || 'Password is required']"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!valid" @click="validateAndSaveUser">Save</v-btn>
          <v-btn @click="closeDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Single Invite Dialog -->
<v-dialog v-model="showInviteDialog" max-width="500px" @keydown.enter="inviteFormValid && sendSingleInvite()" @keydown.esc="showInviteDialog = false">
  <v-card>
    <v-card-title>Invite User</v-card-title>
    <v-card-text>
      <v-form ref="inviteForm" v-model="inviteFormValid">
        <v-text-field
          label="Email"
          v-model="inviteForm.email"
          :rules="[v => !!v || 'Email is required', v => /.+@.+\..+/.test(v) || 'Invalid email']"
          required
        ></v-text-field>
        <v-select
          label="Role"
          v-model="inviteForm.role"
          :items="roleItems"
          item-text="title"
          item-value="name"
          required
        ></v-select>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="primary" :disabled="!inviteFormValid" @click="sendSingleInvite">
        Send Invite
      </v-btn>
      <v-btn @click="showInviteDialog = false">Cancel</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

<!-- Bulk Invite Dialog -->
<v-dialog v-model="showBulkInviteDialog" max-width="600px" @keydown.enter="bulkInviteFormValid && sendBulkInvite()" @keydown.esc="showBulkInviteDialog = false">
  <v-card>
    <v-card-title>Bulk Invite</v-card-title>
    <v-card-text>
      <v-form ref="bulkInviteForm" v-model="bulkInviteFormValid">
        <v-textarea
          label="Emails (separated by delimiter)"
          v-model="bulkInviteForm.emailsText"
          :rows="5"
          :rules="[v => !!v || 'Please enter some emails']"
          required
        ></v-textarea>
        <v-text-field
          label="Delimiter"
          v-model="bulkInviteForm.delimiter"
          hint="e.g. ';'"
        ></v-text-field>
        <v-select
          label="Role"
          v-model="bulkInviteForm.role"
          :items="roleItems"
          item-text="title"
          item-value="name"
          required
        ></v-select>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="primary" :disabled="!bulkInviteFormValid" @click="sendBulkInvite">
        Send Bulk Invite
      </v-btn>
      <v-btn @click="showBulkInviteDialog = false">Cancel</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>


  </v-container>
</template>

<script>
import api from '@/plugins/axios';
import defaultAvatar from '@/assets/default-avatar.png';
export default {
  data() {
    return {
      search: '',
      activeView: 'users',
      itemsPerPage: 10,
      users: [],
      showCreateUserDialog: false,
      valid: true,
      form: {
        id: null,
        username: '',
        email: '',
        roles: [],
        password: '',
      },
      isEditing: false,
      roles: [
        { title: 'Project Leader', value: 'project_leader' },
        { title: 'Transcriber', value: 'transcriber' },
        { title: 'Administrator', value: 'administrator' },
      ],
      headers: [
        { title: 'Username', value: 'username' },
        { title: 'Email', value: 'email' },
        { title: 'Roles', value: 'roles' },
        { title: 'Actions', value: 'actions', sortable: false },
      ],

      showInviteDialog: false,
      showBulkInviteDialog: false,
      inviteFormValid: false,
      bulkInviteFormValid: false,
      inviteForm: {
        email: '',
        role: '',
      },
      bulkInviteForm: {
        emailsText: '',
        delimiter: ';',
        role: '',
      },
      invitations: [],
      inviteHeaders: [
        { title: 'Email', value: 'email', sortable: true },
        { title: 'Role', value: 'role' },
        { title: 'Project', value: 'project', sortable: false },
        { title: 'Status', value: 'is_active' },
        { title: 'Expires At', value: 'expires_at', sortable: true },
        { title: 'Actions', value: 'actions', sortable: false },
      ],
      roleItems: [
        { title: 'Project Leader', name: 'project_leader' },
        { title: 'Transcriber', name: 'transcriber' },
        { title: 'Administrator', name: 'administrator' },
      ],
    };
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.global.current.dark;
    },
  },
  mounted() {
    this.fetchUsers();
    this.fetchInvitations();
  },
  methods: {
    formatDate(dateString) {
    if (!dateString) return ''
    return new Date(dateString).toLocaleString()
  },
    openInviteDialog() {
      this.inviteForm = { email: '', role: '' };
      this.showInviteDialog = true;
    },
    openBulkInviteDialog() {
      this.bulkInviteForm = { emailsText: '', delimiter: ';', role: '' };
      this.showBulkInviteDialog = true;
    },

    // single invite
    async sendSingleInvite() {
      if (!this.inviteForm.email || !this.inviteForm.role) return;
      try {
        const response = await api.post('/api/invitations/single/', {
          email: this.inviteForm.email,
          role: this.inviteForm.role
        });
        console.log(response.data);
        this.showInviteDialog = false;
        this.fetchInvitations();
      } catch (error) {
        console.error(error);
      }
    },

    // bulk invite
    async sendBulkInvite() {
      if (!this.bulkInviteForm.emailsText || !this.bulkInviteForm.role) return;
      try {
        const response = await api.post('/api/invitations/bulk/', {
          emails_text: this.bulkInviteForm.emailsText,
          delimiter: this.bulkInviteForm.delimiter,
          role: this.bulkInviteForm.role,
        });
        console.log(response.data);
        this.showBulkInviteDialog = false;
        this.fetchInvitations();
      } catch (error) {
        console.error(error);
      }
    },

    // fetch invitations
    async fetchInvitations() {
      //if (!this.isAdmin) return;
      try {
        const response = await api.get('/api/invitations/');
        this.invitations = response.data;
      } catch (error) {
        console.error('Error fetching invitations:', error);
      }
    },

    inviteStatus(invite) {
      if (!invite.is_active) {
        return { text: 'Used/Inactive', color: 'red', icon: 'mdi-close' };
      }
      if (invite.has_expired) {
        return { text: 'Expired', color: 'orange', icon: 'mdi-clock-alert-outline' };
      }
      return { text: 'Active', color: 'green', icon: 'mdi-check' };
    },

    // cancel invitation
    async cancelInvite(invitationId) {
      if (!confirm('Are you sure you want to cancel this invitation?')) return;
      try {
        await api.post(`/api/invitations/${invitationId}/cancel/`);
        this.fetchInvitations();
      } catch (error) {
        console.error(error);
      }
    },
    getImageUrl(imagePath) {
      const baseURL = api.defaults.baseURL || "http://localhost:8000"; // Use Axios base URL
       console.log(imagePath); // Verify the image path
       console.log("Base URL:", baseURL); // Verify the base URL
       console.log("Resolved URL:", imagePath ? `${baseURL}${imagePath}` :  defaultAvatar);
      return imagePath ? `${baseURL}${imagePath}` :  defaultAvatar; // Default avatar if null or missing
    },
    async fetchUsers() {
      try {
        const response = await api.get('/api/users/');
        this.users = response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    createUser() {
      this.isEditing = false;
      this.form = { id: null, username: '', email: '', roles: [], password: '' };
      this.showCreateUserDialog = true;
    },
    editUser(user) {
      this.isEditing = true;
      this.form = {
        id: user.id,
        username: user.username,
        email: user.email,
        roles: [...user.roles],
        password: '',
      };
      this.showCreateUserDialog = true;
    },
    closeDialog() {
      this.showCreateUserDialog = false;
    },
    validateAndSaveUser() {
      if (!this.form.username) {
        alert('Username is required');
        return;
      }
      if (!this.form.email || !/.+@.+\..+/.test(this.form.email)) {
        alert('Valid email is required');
        return;
      }
      if (!this.form.roles.length) {
        alert('At least one role is required');
        return;
      }
      if (!this.isEditing && !this.form.password) {
        alert('Password is required for new users');
        return;
      }
      this.saveUser();
    },
    async saveUser() {
      try {
        if (this.isEditing) {
          const payload = {
            username: this.form.username,
            email: this.form.email,
            roles: this.form.roles,
          };
          if (this.form.password) {
            payload.password = this.form.password;
          }
          await api.patch(`/api/users/${this.form.id}/`, payload);
        } else {
          await api.post('/api/users/', this.form);
        }
        this.closeDialog();
        this.fetchUsers();
      } catch (error) {
        console.error('Error saving user:', error);
      }
    },
    async deleteUser(userId) {
      if (confirm('Are you sure you want to delete this user?')) {
        try {
          await api.delete(`/api/users/${userId}/`);
          this.fetchUsers();
        } catch (error) {
          console.error('Error deleting user:', error);
        }
      }
    },
    getRoleColor(role) {
      switch (role.toLowerCase()) {
        case 'project_leader':
          return 'blue';
        case 'administrator':
          return 'red';
        case 'transcriber':
          return 'green';
        default:
          return 'grey';
      }
    },
    getRoleIcon(role) {
      switch (role.toLowerCase()) {
        case 'project_leader':
          return 'mdi-account-star';
        case 'administrator':
          return 'mdi-shield-account';
        case 'transcriber':
          return 'mdi-pencil';
        default:
          return 'mdi-help-circle';
      }
    },
    getRoleText(role) {
      return role
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    goToHome() {
      this.$router.push(`/`);
    },
  },
};
</script>

<style scoped>

/* Delete Button */
.delete-btn {
  background-color: rgb(var(--v-theme-surface));
  border-radius: 0%; /* Square shape */
  width: 40px !important; /* Force width */
  height: 40px !important; /* Force height */
  border: 1px solid #c62828; /* Optional border for delete */
  padding: 0 !important; /* Remove extra padding */
}

.delete-btn:hover {
  background-color: rgba(198, 40, 40, 0.15);
}

/* Edit Button */
.edit-btn {
  background-color: rgb(var(--v-theme-surface));
  border-radius: 0%; /* Square shape */
  width: 40px !important; /* Force width */
  height: 40px !important; /* Force height */
  border: 1px solid #00acc1; /* Optional border for edit */
  padding: 0 !important; /* Remove extra padding */
}

.edit-btn:hover {
  background-color: rgba(0, 172, 193, 0.15);
}

/* Cancel Invite Button */
.cancel-invite-btn {
  background-color: rgb(var(--v-theme-surface));
  border-radius: 0%; /* Square shape */
  width: 40px !important; /* Force width */
  height: 40px !important; /* Force height */
  border: 1px solid #c62828; /* Optional border for cancel invite */
  padding: 0 !important; /* Remove extra padding */
}

.cancel-invite-btn:hover {
  background-color: rgba(198, 40, 40, 0.15);
}

.v-data-table >>> th {
  background-color: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface));
  font-weight: bold !important;
  font-size: 14px;
}
.circle-image {
  object-fit: cover; /* Ensures the image covers the entire circle */
  border-radius: 50%; /* Makes the image circular */
  width: 100%; /* Fills the parent container */
  height: 100%; /* Fills the parent container */
  display: block; /* Ensures the image behaves as a block element */
}
</style>
